import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, Alert, IconButton, CircularProgress, TableContainer
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import api from '../../services/api';
import Layout from '../../components/Layout';

const FEATURE_KEYS = ['auto_reminders', 'manual_reminders', 'custom_branding', 'priority_support', 'api_access'];

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPlan, setEditPlan] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/plans');
      setPlans(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setError('Failed to load plans'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setEditPlan(p);
    setEditForm({
      name: p.name,
      monthly_price_cents: p.monthly_price_cents,
      max_active_contracts: p.max_active_contracts ?? '',
      is_active: p.is_active,
      sort_order: p.sort_order,
      feature_flags: { ...(p.feature_flags || {}) },
    });
  };

  const save = async () => {
    if (!editPlan) return;
    setSaving(true); setError('');
    try {
      const body = {
        name: editForm.name,
        monthly_price_cents: Number(editForm.monthly_price_cents),
        max_active_contracts: editForm.max_active_contracts === '' ? null : Number(editForm.max_active_contracts),
        feature_flags: editForm.feature_flags,
        is_active: editForm.is_active,
        sort_order: Number(editForm.sort_order),
      };
      await api.put(`/admin/plans/${editPlan.id}`, body);
      setEditPlan(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e', mb: 0.5 }}>Subscription plans</Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
          Adjust prices, contract limits, and feature flags. Changes apply immediately.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? <CircularProgress /> : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f5f6fa' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Slug</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Price/mo</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Max active contracts</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Features</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Active</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Edit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map(p => (
                  <TableRow key={p.id} hover>
                    <TableCell><Chip size="small" label={p.slug} /></TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{p.name}</TableCell>
                    <TableCell>${(p.monthly_price_cents/100).toFixed(0)}</TableCell>
                    <TableCell>{p.max_active_contracts ?? '∞'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {Object.entries(p.feature_flags || {}).filter(([k, v]) => v).map(([k]) => (
                          <Chip key={k} size="small" label={k.replace(/_/g, ' ')} sx={{ fontSize: 10 }} />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={p.is_active ? 'success' : 'default'} label={p.is_active ? 'Yes' : 'No'} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(p)}><EditIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Dialog open={!!editPlan} onClose={() => !saving && setEditPlan(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e' }}>Edit plan</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {editPlan && (
            <>
              <TextField label="Name" fullWidth size="small" sx={{ mb: 2 }} value={editForm.name || ''}
                onChange={e => setEditForm({...editForm, name: e.target.value})} />
              <TextField label="Monthly price (cents)" type="number" fullWidth size="small" sx={{ mb: 2 }}
                value={editForm.monthly_price_cents ?? 0} helperText={`$${(editForm.monthly_price_cents/100).toFixed(2)}/mo`}
                onChange={e => setEditForm({...editForm, monthly_price_cents: e.target.value})} />
              <TextField label="Max active contracts (blank = unlimited)" type="number" fullWidth size="small" sx={{ mb: 2 }}
                value={editForm.max_active_contracts}
                onChange={e => setEditForm({...editForm, max_active_contracts: e.target.value})} />
              <TextField label="Sort order" type="number" fullWidth size="small" sx={{ mb: 2 }}
                value={editForm.sort_order ?? 0}
                onChange={e => setEditForm({...editForm, sort_order: e.target.value})} />
              <FormControlLabel control={<Switch checked={!!editForm.is_active} onChange={e => setEditForm({...editForm, is_active: e.target.checked})} />} label="Active" sx={{ mb: 1 }} />

              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mt: 2, mb: 1, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Feature flags</Typography>
              {FEATURE_KEYS.map(k => (
                <FormControlLabel key={k} sx={{ display: 'block' }}
                  control={<Switch checked={!!editForm.feature_flags?.[k]}
                    onChange={e => setEditForm({...editForm, feature_flags: {...editForm.feature_flags, [k]: e.target.checked}})} />}
                  label={k.replace(/_/g, ' ')}
                />
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditPlan(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving} sx={{ bgcolor: '#1a237e' }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
